import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Search, X, Loader2, MapPin } from "lucide-react";

/**
 * Type-to-search picker for "which property does this belong to".
 *
 * Replaces the plain <select> the Buyer Enquiries holding area used to file an
 * enquiry onto a listing. That select had two problems (Christian Gravias,
 * Monday 12849654308, 2026-08-19):
 *
 *   1. It called the listings feed without `agency_wide`, so it only ever
 *      offered the properties assigned to the logged-in user - "I think its
 *      only showing properties assigned to me". Every other listing in the CRM
 *      was unreachable, which is exactly the case that matters when a Meta lead
 *      lands on somebody else's listing.
 *   2. It labelled each option `title || address`, so the list read as marketing
 *      headlines ("PRIME PRESTON FREEHOLD INVESTMENT | SECURELY LEASED") with no
 *      address anywhere - "its only showing the heading of the properties. i am
 *      not sure which one to choose".
 *
 * So: search server-side across every listing in the CRM, and lead with the
 * address. The headline stays as the second line, because that is how agents
 * recognise the campaign a lead came from.
 */

const PAGE_SIZE = 25;
const MIN_QUERY = 2;

/** The address to show for a row, falling back through the address fields. */
export const propertyLabel = (p) => {
  if (!p) return "";
  const direct = p.full_address || p.address;
  if (direct) return String(direct);
  const parts = [
    [p.street_number, p.street_name || p.street].filter(Boolean).join(" "),
    p.suburb || p.city,
    p.state,
    p.postcode || p.postal_code,
  ].filter(Boolean);
  return parts.join(", ") || p.title || `Property #${p.id}`;
};

/** The marketing headline, only when it adds something the address does not. */
const propertySubLabel = (p) => {
  const title = (p?.title || "").trim();
  if (!title) return "";
  return title === propertyLabel(p) ? "" : title;
};

const statusName = (p) => {
  const status = p?.status;
  if (!status) return "";
  return typeof status === "object" ? status.name || "" : String(status);
};

const PropertySearchPicker = ({
  selected,
  onSelect,
  placeholder = "Search every property in the CRM by address or suburb...",
  /** Extra query params, e.g. { include_appraisals: true }. */
  params,
  /** The listings feed to search, normally `getProperties` from api/agent.
   *  Injected rather than imported so this component pulls no api module (and
   *  therefore no axios) into the module graph of whatever renders it. */
  fetchProperties,
  inputClassName = "",
  disabled = false,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [error, setError] = useState("");
  const listId = `property-picker-${useId()}`;

  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  // Only the newest request is allowed to write into state, so a slow response
  // for "39" can never overwrite the results for "39 beech".
  const requestRef = useRef(0);

  // Keyed off the serialised params so an inline `params={{...}}` object from the
  // caller cannot make `runSearch` a new function on every render, which would
  // re-fire the priming effect in a loop.
  const paramsKey = JSON.stringify(params || {});
  const extraParams = useMemo(() => JSON.parse(paramsKey), [paramsKey]);
  const fetcherRef = useRef(fetchProperties);
  fetcherRef.current = fetchProperties;

  const runSearch = useCallback(
    (text) => {
      const requestId = requestRef.current + 1;
      requestRef.current = requestId;
      if (typeof fetcherRef.current !== "function") return;
      setLoading(true);
      setError("");
      fetcherRef.current({
        // Every listing in the CRM, not just the ones assigned to this user.
        agency_wide: true,
        page_size: PAGE_SIZE,
        ...extraParams,
        ...(text ? { search: text } : {}),
      })
        .then((res) => {
          if (requestRef.current !== requestId) return;
          const data = res?.data;
          const list = Array.isArray(data) ? data : data?.results || [];
          setResults(Array.isArray(list) ? list : []);
          setHighlight(-1);
        })
        .catch(() => {
          if (requestRef.current !== requestId) return;
          setResults([]);
          setError("Could not load properties. Try again.");
        })
        .finally(() => {
          if (requestRef.current === requestId) setLoading(false);
        });
    },
    [extraParams]
  );

  // Prime the list with the most recent listings so the picker is usable
  // without typing anything, the way the old dropdown was.
  useEffect(() => {
    runSearch("");
  }, [runSearch]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

  const handleChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = text.trim();
    if (trimmed && trimmed.length < MIN_QUERY) return;
    debounceRef.current = setTimeout(() => runSearch(trimmed), 300);
  };

  const pick = (property) => {
    onSelect && onSelect(property);
    setOpen(false);
    setHighlight(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((i) => (i < results.length - 1 ? i + 1 : i));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((i) => (i > 0 ? i - 1 : -1));
    } else if (e.key === "Enter") {
      // Never let picking a property submit the surrounding form.
      e.preventDefault();
      if (highlight >= 0 && highlight < results.length) pick(results[highlight]);
    }
  };

  if (selected) {
    return (
      <div className="flex items-start gap-3 w-full px-4 py-3 bg-[#55C5D0]/5 border border-[#55C5D0]/30 rounded-xl">
        <MapPin size={16} className="text-[#55C5D0] mt-0.5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-gray-800 break-words">{propertyLabel(selected)}</div>
          {propertySubLabel(selected) && (
            <div className="text-xs text-gray-500 mt-0.5 break-words">{propertySubLabel(selected)}</div>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            onSelect && onSelect(null);
            setQuery("");
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          className="text-gray-400 hover:text-gray-700 flex-shrink-0"
          aria-label="Clear selected property"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-label="Search properties"
          value={query}
          disabled={disabled}
          onChange={handleChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className={
            inputClassName ||
            "w-full pl-11 pr-10 py-3 bg-white border border-[#E4E6E9] rounded-xl text-sm font-medium text-gray-800 outline-none focus:ring-4 focus:ring-[#55C5D0]/10 transition-all"
          }
        />
        {loading && (
          <Loader2 size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#55C5D0] animate-spin" />
        )}
      </div>

      {open && (
        <div
          id={listId}
          className="absolute z-[60] w-full mt-1.5 bg-white rounded-xl border border-gray-200 shadow-xl max-h-72 overflow-y-auto"
        >
          {error && <div className="px-4 py-3 text-xs font-bold text-red-600">{error}</div>}
          {!error && !loading && results.length === 0 && (
            <div className="px-4 py-3 text-xs text-gray-500">
              {query.trim() ? `No property matches "${query.trim()}".` : "No properties found."}
            </div>
          )}
          {results.map((p, index) => (
            <button
              key={p.id}
              type="button"
              onMouseEnter={() => setHighlight(index)}
              onClick={() => pick(p)}
              className={`w-full text-left px-4 py-2.5 border-b border-gray-100 last:border-b-0 transition-colors ${
                index === highlight ? "bg-[#F0FDFE]" : "hover:bg-[#F0FDFE]"
              }`}
            >
              <div className="text-sm font-bold text-gray-800 break-words">{propertyLabel(p)}</div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                {propertySubLabel(p) && (
                  <span className="text-[11px] text-gray-500 break-words">{propertySubLabel(p)}</span>
                )}
                {statusName(p) && (
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[#55C5D0]">
                    {statusName(p)}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PropertySearchPicker;
